'use strict';

/**
 * P4.2 — Notification Bell + Dropdown + List-Page.
 *
 * Stil/Struktur analog user-follow.js (delegated-click + fetch + toastr).
 *
 * Polling-Strategie:
 *   - on page-load: fetch /notifications/unread-count (initial badge)
 *   - on window focus + visibilitychange (max 1x pro 10s)
 *   - on dropdown-open: fetch /notifications/dropdown (last 10 + CSRF tokens) + render
 *   NICHT: setInterval — zu aggressiv fuer low-frequency-Traffic.
 */
export default class Notification {
    initialize() {
        this.bell = document.querySelector('.js-notification-bell-toggle');
        if (!this.bell) {
            return; // Gast / kein Bell gerendert
        }
        this.badge = document.querySelector('.js-notification-badge');
        this.itemsContainer = document.querySelector('.js-notification-items');
        this.lastFetchTs = 0;

        this.refreshUnreadCount();

        window.addEventListener('focus', () => this.refreshUnreadCount(10000));
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                this.refreshUnreadCount(10000);
            }
        });

        // Bell-Klick -> Dropdown laden (Bootstrap-collapse macht das Aufklappen).
        // href verweist auf /notifications fuer noscript-Fallback.
        this.bell.addEventListener('click', (e) => {
            const collapseTarget = document.querySelector('#notificationDropdown');
            if (collapseTarget && collapseTarget.classList.contains('show')) {
                return; // wird gerade geschlossen
            }
            e.preventDefault();
            this.loadDropdown();
        });

        // Delegated: in dieser Reihenfolge — Delete-Aktionen FIRST damit sie nicht
        // mit mark-read-on-item-click kollidieren (Trash sitzt INNERHALB des <li>).
        document.addEventListener('click', (e) => {
            const delOne = e.target.closest('.js-notification-delete');
            if (delOne) {
                e.preventDefault();
                e.stopPropagation();
                this.deleteOne(delOne);
                return;
            }
            const delRead = e.target.closest('.js-notification-delete-read');
            if (delRead) {
                e.preventDefault();
                this.deleteAllRead(delRead);
                return;
            }
            const item = e.target.closest('.js-notification-item');
            if (item) {
                this.markRead(item);
                return;
            }
            const bulk = e.target.closest('.js-notification-mark-all');
            if (bulk) {
                e.preventDefault();
                this.markAllRead(bulk);
            }
        });
    }

    deleteOne(button) {
        const url = button.dataset.deleteUrl;
        const token = button.dataset.token;
        if (!url || !token) { return; }
        fetch(url, {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ _token: token })
        }).then(r => r.json()).then(data => {
            if (parseInt(data.error, 10) === 0) {
                const item = button.closest('.js-notification-item');
                if (item) { item.remove(); }
                this.renderBadge(parseInt(data.count || 0, 10));
            } else if (typeof toastr !== 'undefined') {
                toastr.error(data.message || 'Löschen fehlgeschlagen');
            }
        }).catch(() => {});
    }

    deleteAllRead(button) {
        if (!confirm('Alle gelesenen Benachrichtigungen wirklich löschen?')) {
            return;
        }
        const url = button.dataset.deleteReadUrl;
        const token = button.dataset.token;
        if (!url || !token) { return; }
        fetch(url, {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ _token: token })
        }).then(r => r.json()).then(data => {
            if (parseInt(data.error, 10) === 0) {
                // Auf der Liste: gelesene Items aus DOM entfernen (= alles ohne fw-bold).
                document.querySelectorAll('.js-notification-item').forEach(el => {
                    if (!el.classList.contains('fw-bold')) { el.remove(); }
                });
                this.renderBadge(parseInt(data.count || 0, 10));
                if (typeof toastr !== 'undefined') {
                    toastr.success((data.deleted || 0) + ' gelöscht.');
                }
            }
        }).catch(() => {});
    }

    refreshUnreadCount(throttleMs = 0) {
        const now = Date.now();
        if (throttleMs > 0 && (now - this.lastFetchTs) < throttleMs) {
            return;
        }
        this.lastFetchTs = now;
        fetch(this.bell.dataset.unreadCountUrl, {
            headers: { 'Accept': 'application/json' }
        }).then(r => r.json())
          .then(data => this.renderBadge(parseInt(data.count || 0, 10)))
          .catch(() => {});
    }

    renderBadge(count) {
        if (!this.badge) { return; }
        if (count > 0) {
            this.badge.textContent = count > 99 ? '99+' : String(count);
            this.badge.style.display = '';
        } else {
            this.badge.style.display = 'none';
        }
    }

    loadDropdown() {
        if (!this.itemsContainer) { return; }
        fetch(this.bell.dataset.dropdownUrl, {
            headers: { 'Accept': 'application/json' }
        }).then(r => r.json())
          .then(data => {
              this.renderBadge(parseInt(data.count || 0, 10));
              if (!data.items || data.items.length === 0) {
                  this.itemsContainer.innerHTML = '<div class="dropdown-item text-muted">Keine Benachrichtigungen.</div>';
                  return;
              }
              this.itemsContainer.innerHTML = data.items.map(n => {
                  const cls = n.isRead ? '' : 'fw-bold';
                  const markUrl = '/notification/' + n.id + '/read';
                  return '<div class="dropdown-item js-notification-item ' + cls + '"' +
                         ' data-notification-id="' + n.id + '"' +
                         ' data-mark-read-url="' + markUrl + '"' +
                         ' data-token="' + this.escape(n.token) + '">' +
                         this.renderItemBody(n) + '</div>';
              }).join('');
              // Bulk-Button frischen Token zuweisen (Dropdown + List-Page beide js-notification-mark-all)
              document.querySelectorAll('.js-notification-mark-all').forEach(btn => {
                  btn.dataset.readAllUrl = this.bell.dataset.readAllUrl;
                  btn.dataset.token = data.tokenReadAll;
              });
          }).catch(() => {
              this.itemsContainer.innerHTML = '<div class="dropdown-item text-danger">Fehler beim Laden.</div>';
          });
    }

    markRead(item) {
        const id = item.dataset.notificationId;
        const url = item.dataset.markReadUrl;
        const token = item.dataset.token;
        if (!id || !url || !token) { return; }
        fetch(url, {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ _token: token }),
            keepalive: true  // ueberlebt die Navigation, wenn ein Link im Item geklickt wird
        }).then(r => r.json()).then(data => {
            if (parseInt(data.error, 10) === 0) {
                item.classList.remove('fw-bold');
                this.renderBadge(parseInt(data.count || 0, 10));
            }
        }).catch(() => {});
    }

    markAllRead(button) {
        const url = button.dataset.readAllUrl;
        const token = button.dataset.token;
        if (!url || !token) { return; }
        fetch(url, {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ _token: token })
        }).then(r => r.json()).then(data => {
            if (parseInt(data.error, 10) === 0) {
                document.querySelectorAll('.js-notification-item').forEach(el => el.classList.remove('fw-bold'));
                this.renderBadge(0);
                if (typeof toastr !== 'undefined') {
                    toastr.success('Alle als gelesen markiert.');
                }
            }
        }).catch(() => {});
    }

    /**
     * Rendert den Dropdown-Text mit Links: Username-Prefix → Profil des Actors,
     * Rest → Ziel-Entity (Bild/Story). Fallback: bei System-Notifications ohne
     * Actor bzw. wenn die Message nicht mit dem Username beginnt, plain bzw. nur
     * Entity-Link.
     */
    renderItemBody(n) {
        const msg = String(n.message || '');
        const user = n.actorUsername || '';
        const entityLink = (text) => n.entityUrl
            ? '<a href="' + this.escape(n.entityUrl) + '" class="text-reset text-decoration-none">' + this.escape(text) + '</a>'
            : this.escape(text);
        if (user && n.actorUrl && msg.indexOf(user) === 0) {
            const userLink = '<a href="' + this.escape(n.actorUrl) + '" class="fw-semibold">' + this.escape(user) + '</a>';
            return userLink + entityLink(msg.slice(user.length));
        }
        return entityLink(msg);
    }

    escape(s) {
        const div = document.createElement('div');
        div.textContent = String(s || '');
        return div.innerHTML;
    }
}
